import React, { useEffect, useState } from "react";
import { useGlobalContext } from "../context/globalContext";
import { Toaster, toast } from "react-hot-toast";
import Loading from "../components/Loading";
import UserOverallStatsCard from "../components/UserOverallStatsCard";
import { useNavigate } from "react-router-dom";

export default function MonthlyUserHistory() {
  const { userInfo } = useGlobalContext();
  const navigate = useNavigate();

  const [fetchingData, setFetchingData] = useState(false);
  const [fetchingAgents, setFetchingAgents] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agents, setAgents] = useState([]);
  const [monthlyHistory, setMonthlyHistory] = useState(null);

  const API_BASE_URL = "https://66z6lhu4ge.execute-api.ap-south-1.amazonaws.com/prod/call";

  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];

  const getAgents = async () => {
    setFetchingAgents(true);
    try {
      const response = await fetch(`${API_BASE_URL}/get-agent-data`);
      const data = await response.json();
      if (data) {
        setAgents(data); // Array of { name, agent_number }
      }
    } catch (err) {
      console.error("Error fetching agents:", err);
      toast.error("Failed to fetch agents");
    } finally {
      setFetchingAgents(false);
    }
  };

  const getMonthlyHistory = async () => {
    if (!selectedAgent) {
      toast.error("Please select an agent first");
      return;
    }

    setFetchingData(true);
    try {
      // Get current date for end_date and calculate start_date as 12 months ago
      const endDate = new Date().toISOString().split('T')[0];
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 12);
      const formattedStartDate = startDate.toISOString().split('T')[0];

      const queryParams = new URLSearchParams({
        agent_number: selectedAgent.agent_number,
        start_date: formattedStartDate,
        end_date: endDate
      });

      const response = await fetch(`${API_BASE_URL}/get-data?${queryParams}`);
      const data = await response.json();

      if (data) {
        // Process the data to group by month and year
        const monthlyData = processMonthlyData(data);
        setMonthlyHistory(monthlyData);
      } else {
        toast.error("No data found");
      }
    } catch (err) {
      console.error("Error fetching monthly history:", err);
      toast.error("Failed to fetch monthly history");
    } finally {
      setFetchingData(false);
    }
  };

  const processMonthlyData = (data) => {
    // Group data by month and year
    const monthlyStats = data.reduce((acc, record) => {
      const date = new Date(record.date);
      const month = date.getMonth();
      const year = date.getFullYear();
      const key = `${year}-${month}`;

      if (!acc[key]) {
        acc[key] = {
          targetMonth: month,
          targetYear: year,
          totalCallCount: 0,
          totalConnectedCount: 0,
          totalChatCount: 0,
          totalCount: 0
        };
      }

      // Update counts based on the record
      acc[key].totalCallCount += record.total_calls || 0;
      acc[key].totalConnectedCount += (record.ic_answered || 0) + (record.oc_answered || 0);
      acc[key].totalCount += record.total_calls || 0;

      return acc;
    }, {});

    // Convert to array and sort by date
    return Object.values(monthlyStats).sort((a, b) => {
      return b.targetYear - a.targetYear || b.targetMonth - a.targetMonth;
    });
  };

  useEffect(() => {
    if (userInfo?.isAdmin === false) {
      toast.error("You are not authorized to view this page");
      navigate("/");
    }
    getAgents();
  }, [userInfo?.isAdmin]);

  return (
    <>
      <div className="px-4">
        <h1 className="text-xl font-semibold w-full my-2">
          Select Agent to view Monthly History
        </h1>
        <div className="mb-3">
          {fetchingAgents ? (
            <i className="fas fa-spinner fa-spin text-4xl text-theme-yellow-dark"></i>
          ) : (
            <div className="flex items-center gap-2 w-full">
              <select
                className="w-max p-2 rounded-md border-2 border-gray-400"
                onChange={(e) => {
                  const agent = agents.find(a => a.agent_number === e.target.value);
                  setSelectedAgent(agent);
                }}
                defaultValue=""
              >
                <option value="" disabled>Select Agent</option>
                {agents.map((agent) => (
                  <option key={agent.agent_number} value={agent.agent_number}>
                    {agent.name}
                  </option>
                ))}
              </select>
              <button
                className="bg-theme-yellow-dark text-white p-2 rounded-md w-24"
                onClick={getMonthlyHistory}
                disabled={!selectedAgent}
              >
                {fetchingData ? (
                  <i className="fas fa-spinner fa-spin"></i>
                ) : (
                  "Get Data"
                )}
              </button>
            </div>
          )}
        </div>

        <div className="py-3">
          {fetchingData ? (
            <Loading />
          ) : monthlyHistory ? (
            <>
              <div className="px-3 my-3 text-xl w-full text-center">
                Monthly history for {selectedAgent?.name}
              </div>
              <div className="flex flex-wrap">
                {monthlyHistory.map((item, index) => {
                  const userData = {
                    userName: selectedAgent?.name,
                    totalCallCount: item.totalCallCount,
                    totalConnectedCount: item.totalConnectedCount,
                    totalChatCount: item.totalChatCount,
                    totalCount: item.totalCount,
                  };
                  const targetMonthName = monthNames[item.targetMonth];
                  return (
                    <div key={index} className="my-2">
                      <div className="-mb-2 w-full text-center">
                        {targetMonthName}, {item.targetYear}
                      </div>
                      <UserOverallStatsCard
                        userData={userData}
                        type="monthly"
                      />
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div>No Data to show</div>
          )}
        </div>
      </div>
    </>
  );
}